'use client';

import { Form, Formik, type FormikProps } from 'formik';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useId } from 'react';

import { Button } from '@commons/components/Button';
import { FormikConsentCheckbox as ConsentCheckbox } from '@commons/components/Form/Consent';
import { FieldGroup } from '@commons/components/Form/FieldGroup';
import { FormikCountrySelect as CountrySelect } from '@commons/components/Form/Select/Country';
import { FormikProvinceSelect as ProvinceSelect } from '@commons/components/Form/Select/Province';
import { FormikStateSelect as StateSelect } from '@commons/components/Form/Select/State';
import FormStatus from '@commons/components/Form/Status';
import { FormikTextarea as Textarea } from '@commons/components/Form/TextArea';
import { FormikTextField as TextField } from '@commons/components/Form/TextField';
import TrackingFields, {
  TRACKING_FIELDS_INITIAL_VALUES,
  type TrackingFieldsValues,
} from '@commons/components/Form/Tracking';
import { useValidation } from '@commons/components/Form/validation';
import Flex from '@commons/containers/Flex';
import type { WebformProps } from '@commons/pagebuilder/webforms';
import { useFormSubmit } from '@commons/services/form';

import { AIIA_WEBFORM_ID } from '..';
import ContactUsFormHeader from './Header';
import ContactUsFormThankYou from './ThankYou';

// Type definition for the values in the form.

type FormValues = TrackingFieldsValues & {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  job_title?: string;
  company_name: string;
  country: string;
  message: string;
  consent: boolean;
};

// Constant for the form type because it is used in multiple places.

const FORM_TYPE = 'contact_aiia';

// Initial values for each field in the form.

const initialValues: FormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  job_title: '',
  company_name: '',
  country: '',
  message: '',
  consent: false,
  ...TRACKING_FIELDS_INITIAL_VALUES,
  form_type: FORM_TYPE,
};

/**
 * Contact us form component.
 */

const ContactUsForm = (props: WebformProps) => {
  const t = useTranslations('forms');
  const pathname = usePathname();
  const consentId = useId();

  const { shape, input, email, phone, consent } = useValidation();

  const validationSchema = shape({
    first_name: input,
    last_name: input,
    email: email,
    phone_number: phone,
    job_title: input.optional(),
    company_name: input,
    country: input,
    message: input.max(10000),
    consent: consent,
  });

  const { inDrawer, drawerHeader, thankYou, onSuccess } = props;

  const submitLabel = props.submitLabel || t('contactUs.submit');

  const handleSubmit = useFormSubmit<FormValues>({
    webformId: AIIA_WEBFORM_ID.ContactUs,
    onSuccess,
  });

  initialValues.url_source = pathname;

  const renderedHeader = inDrawer ? (drawerHeader ?? <ContactUsFormHeader />) : null;
  const renderedThankYou = thankYou ? thankYou : <ContactUsFormThankYou />;

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<FormValues>) => {
        const { values, status, isSubmitting, setFieldValue } = props;

        const isSubmitted = status && status.state === 'success';
        if (isSubmitted) {
          return renderedThankYou;
        }

        return (
          <Form id={AIIA_WEBFORM_ID.ContactUs} className="w-100">
            {!isSubmitted && renderedHeader}
            <Flex direction={{ xs: 'column', lg: 'row' }} gap={{ xs: 'none', lg: 'xxs' }}>
              <FieldGroup spacing="xxs" width={{ xs: 12, lg: 6 }} name="first_name">
                <TextField
                  variant="secondary"
                  required
                  id="first_name"
                  name="first_name"
                  label={t('fields.first_name.placeholder')}
                />
              </FieldGroup>
              <FieldGroup spacing="xxs" width={{ xs: 12, lg: 6 }} name="last_name">
                <TextField
                  variant="secondary"
                  required
                  id="last_name"
                  name="last_name"
                  label={t('contactUs.lastName.placeholder')}
                />
              </FieldGroup>
            </Flex>
            <FieldGroup name="email" spacing="xxs">
              <TextField
                required
                variant="secondary"
                id="email"
                name="email"
                label={t('fields.email.placeholder')}
              />
            </FieldGroup>
            <FieldGroup name="phone_number" spacing="xxs">
              <TextField
                id="phone_number"
                required
                variant="secondary"
                name="phone_number"
                label={t('fields.phone_number.placeholder')}
              />
            </FieldGroup>
            <FieldGroup name="job_title" spacing="xxs">
              <TextField
                id="job_title"
                variant="secondary"
                name="job_title"
                label={t('contactUs.jobTitle.placeholder')}
              />
            </FieldGroup>
            <FieldGroup name="company_name" spacing="xxs">
              <TextField
                required
                variant="secondary"
                id="company_name"
                name="company_name"
                label={t('contactUs.organization.placeholder')}
              />
            </FieldGroup>
            <FieldGroup spacing="xxs" name="country">
              <CountrySelect
                variant="secondary"
                required
                id="country"
                useLabelAsValue={false}
                name="country"
                placeholder={t('fields.country.placeholder')}
                onValueChange={(value) => {
                  setFieldValue('country', value);
                  // Reset province/state when country changes
                  if (value !== 'CA') setFieldValue('province', '');
                  if (value !== 'US') setFieldValue('state', '');
                }}
              />
            </FieldGroup>
            {values.country === 'CA' && (
              <FieldGroup spacing="xxs" name="province">
                <ProvinceSelect
                  variant="secondary"
                  required
                  id="province"
                  name="province"
                  placeholder={t('fields.province.placeholder')}
                />
              </FieldGroup>
            )}
            {values.country === 'US' && (
              <FieldGroup spacing="xs" name="state">
                <StateSelect
                  variant="secondary"
                  required
                  id="state"
                  name="state"
                  placeholder={t('fields.state.placeholder')}
                />
              </FieldGroup>
            )}
            <FieldGroup name="message" spacing="sm">
              <Textarea
                variant="secondary"
                rows={6}
                required
                id="message"
                name="message"
                label={t('fields.message.placeholder')}
              />
            </FieldGroup>
            <TrackingFields formType={FORM_TYPE} />
            <FieldGroup name="consent" gap="xxs" spacing="md">
              <ConsentCheckbox name="consent" id={consentId} />
            </FieldGroup>
            <FormStatus />
            <Button disabled={isSubmitting} variant="secondary" type="submit">
              {submitLabel}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ContactUsForm;
